import api from '../api/apiClient';

/**
 * Management Service
 *
 * Connects to the dedicated Management microservice for Teams, Agents, and RBAC.
 * Nginx routes /management/api/* to the microservice.
 */

const MGT = '/management/api/v1';
const ALERTS = '/alerts/api/v1';

export const managementService = {
  // Keep payloads compatible with stricter backend validators.
  normalizeAgentPayload(agentData = {}) {
    const payload = {
      agent_name: (agentData.agent_name || '').trim(),
    };

    const identifier = (agentData.agent_identifier || '').trim();
    if (identifier) payload.agent_identifier = identifier;

    if (Array.isArray(agentData.category_ids) && agentData.category_ids.length > 0) {
      payload.category_ids = agentData.category_ids
        .map(id => Number(id))
        .filter(id => Number.isInteger(id) && id > 0);
    }

    // Send detection filters only when explicitly set and not default "all".
    if (Array.isArray(agentData.detection_filters)) {
      const filters = agentData.detection_filters
        .filter(Boolean)
        .filter(f => f !== 'all');
      if (filters.length > 0) {
        payload.detection_filters = filters;
      }
    }

    return payload;
  },

  normalizeTeamPayload(teamData = {}) {
    const payload = {
      name: (teamData.name || '').trim(),
    };

    const costCenter = (teamData.cost_center || '').trim();
    const description = (teamData.description || '').trim();
    if (costCenter) payload.cost_center = costCenter;
    if (description) payload.description = description;

    if (Array.isArray(teamData.agent_ids) && teamData.agent_ids.length > 0) {
      const ids = teamData.agent_ids
        .map(id => Number(id))
        .filter(id => Number.isInteger(id) && id > 0);
      if (ids.length > 0) payload.agent_ids = ids;
    }

    return payload;
  },

  // =========================================================================
  // Teams CRUD
  // =========================================================================

  async getTeams() {
    const response = await api.get(`${MGT}/teams`);
    return response.data;
  },

  async getTeam(teamId) {
    const response = await api.get(`${MGT}/teams/${teamId}`);
    return response.data;
  },

  async createTeam(teamData) {
    const payload = this.normalizeTeamPayload(teamData);
    const response = await api.post(`${MGT}/teams`, payload);
    return response.data;
  },

  async updateTeam(teamId, teamData) {
    const payload = this.normalizeTeamPayload(teamData);
    const response = await api.put(`${MGT}/teams/${teamId}`, payload);
    return response.data;
  },

  async deleteTeam(teamId) {
    const response = await api.delete(`${MGT}/teams/${teamId}`);
    return response.data;
  },

  async assignAgentsToTeam(teamId, agentIds) {
    const response = await api.post(`${MGT}/teams/${teamId}/agents`, { agent_ids: agentIds });
    return response.data;
  },

  async removeAgentFromTeam(teamId, agentId) {
    const response = await api.delete(`${MGT}/teams/${teamId}/agents/${agentId}`);
    return response.data;
  },

  // =========================================================================
  // Agent CRUD
  // =========================================================================

  async getAgents() {
    const response = await api.get(`${MGT}/agents`);
    return response.data;
  },

  async getAgent(agentId) {
    const response = await api.get(`${MGT}/agents/${agentId}`);
    return response.data;
  },

  async createAgent(agentData) {
    const payload = this.normalizeAgentPayload(agentData);
    const response = await api.post(`${MGT}/agents`, payload);
    return response.data;
  },

  async updateAgent(agentId, agentData) {
    const payload = this.normalizeAgentPayload(agentData);
    const response = await api.put(`${MGT}/agents/${agentId}`, payload);
    return response.data;
  },

  async deleteAgent(agentId) {
    const response = await api.delete(`${MGT}/agents/${agentId}`);
    return response.data;
  },

  // =========================================================================
  // Business Categories
  // =========================================================================

  async getBusinessCategories() {
    const response = await api.get(`${MGT}/business-categories`);
    return response.data;
  },

  // =========================================================================
  // User Management
  // =========================================================================

  async getUsers() {
    const response = await api.get(`${MGT}/users`);
    return response.data;
  },

  async createUser(userData) {
    const response = await api.post(`${MGT}/users`, userData);
    return response.data;
  },

  async updateUserRole(userId, newRoleName) {
    const response = await api.put(`${MGT}/users/${userId}/role`, { new_role_name: newRoleName });
    return response.data;
  },

  async deleteUser(userId) {
    const response = await api.delete(`${MGT}/users/${userId}`);
    return response.data;
  },

  async getRoles() {
    const response = await api.get(`${MGT}/roles`);
    return response.data;
  },

  // =========================================================================
  // Notification Channels (via Alert Microservice)
  // =========================================================================

  async getNotificationChannels() {
    const response = await api.get(`${ALERTS}/alerts/channels`);
    return response.data;
  },

  async getNotificationChannel(channelId) {
    const response = await api.get(`${ALERTS}/alerts/channels/${channelId}`);
    return response.data;
  },

  async createNotificationChannel(data) {
    const response = await api.post(`${ALERTS}/alerts/channels`, data);
    return response.data;
  },

  async updateNotificationChannel(channelId, data) {
    const response = await api.put(`${ALERTS}/alerts/channels/${channelId}`, data);
    return response.data;
  },

  async deleteNotificationChannel(channelId) {
    const response = await api.delete(`${ALERTS}/alerts/channels/${channelId}`);
    return response.data;
  },

  // =========================================================================
  // Alert Subscriptions (via Alert Microservice)
  // =========================================================================

  async getAlertSubscriptions() {
    const response = await api.get(`${ALERTS}/alerts/subscriptions`);
    // Map backend 'module' to frontend 'alert_category' for display
    return (response.data || []).map(sub => ({
      ...sub,
      alert_category: sub.module === 'all' ? 'Any' : sub.module,
      severity: sub.severity === 'all' ? 'All' : (sub.severity?.charAt(0).toUpperCase() + sub.severity?.slice(1)),
    }));
  },

  async getAlertSubscription(subscriptionId) {
    const response = await api.get(`${ALERTS}/alerts/subscriptions/${subscriptionId}`);
    const sub = response.data;
    // Map backend 'module' field to frontend 'alert_category'
    return {
      ...sub,
      alert_category: sub.module === 'all' ? 'Any' : sub.module,
      severity: sub.severity === 'all' ? 'All' : sub.severity,
    };
  },

  async createAlertSubscription(data) {
    const module = data.alert_category === 'Any' ? 'all' : (data.alert_category || 'all');
    const payload = {
      channel_id: parseInt(data.channel_id),
      module,
      severity: (data.severity || 'All').toLowerCase(),
      status: data.status || 'active',
    };
    const response = await api.post(`${ALERTS}/alerts/subscriptions`, payload);
    return response.data;
  },

  async updateAlertSubscription(subscriptionId, data) {
    const payload = {
      channel_id: data.channel_id ? parseInt(data.channel_id) : undefined,
      module: data.alert_category === 'Any' ? 'all' : data.alert_category,
      severity: data.severity ? data.severity.toLowerCase() : undefined,
      status: data.status,
    };
    const response = await api.put(`${ALERTS}/alerts/subscriptions/${subscriptionId}`, payload);
    return response.data;
  },

  async deleteAlertSubscription(subscriptionId) {
    const response = await api.delete(`${ALERTS}/alerts/subscriptions/${subscriptionId}`);
    return response.data;
  },

  // =========================================================================
  // Alert Categories / Modules (via Alert Microservice)
  // =========================================================================

  async getAlertCategories() {
    const response = await api.get(`${ALERTS}/alerts/modules`);
    // Return module objects with value and label for the dropdown
    // "all" maps to "Any" in the frontend convention
    return response.data.map(m => ({
      value: m.value === 'all' ? 'Any' : m.value,
      label: m.value === 'all' ? 'Any' : m.label,
    }));
  },

  // =========================================================================
  // OTEL Data Policy
  // =========================================================================

  async getDataPolicy() {
    return api.get(`${MGT}/organization/data-policy`);
  },

  async updateDataPolicy(data) {
    return api.put(`${MGT}/organization/data-policy`, data);
  },

  async rotateEncryptionKey() {
    return api.post(`${MGT}/organization/data-policy/rotate`);
  },

  // =========================================================================
  // Integrations / Connectors
  // =========================================================================

  async getIntegrations() {
    const response = await api.get(`${MGT}/integrations`);
    return response.data;
  },

  async getIntegrationPlatforms() {
    const response = await api.get(`${MGT}/integrations/platforms`);
    return response.data;
  },

  /** Dismiss the one-time notice of a system replacement of a retired Code Security AI model. */
  async acknowledgeSastAiModelChange() {
    const response = await api.post(`${MGT}/integrations/sast_ai/model-change/ack`);
    return response.data;
  },

  async uploadIntegrationCredentials(platform, credentials, enabled = true, integrationKey = null) {
    const response = await api.post(`${MGT}/integrations/${platform}/credentials`, {
      platform,
      enabled,
      credentials,
      ...(integrationKey ? { integration_key: integrationKey } : {}),
    });
    return response.data;
  },

  // Pre-flight test of *inline* (unsaved) credentials — probes the platform
  // without persisting anything to MySQL or Secret Manager. Backs the connect
  // wizards' "Test connection" button. Distinct from verifyIntegrationCredentials,
  // which checks the credentials already stored in Secret Manager.
  async testIntegrationCredentials(platform, credentials, enabled = true, integrationKey = null) {
    const response = await api.post(`${MGT}/integrations/${platform}/test`, {
      platform,
      enabled,
      credentials,
      ...(integrationKey ? { integration_key: integrationKey } : {}),
    });
    return response.data;
  },

  async deleteIntegrationCredentials(platform, integrationKey = 'default') {
    const response = await api.delete(`${MGT}/integrations/${platform}/credentials`, {
      params: { integration_key: integrationKey },
    });
    return response.data;
  },

  async setIntegrationEnabled(platform, enabled, integrationKey = 'default') {
    const response = await api.patch(`${MGT}/integrations/${platform}/enabled`, { enabled }, {
      params: { integration_key: integrationKey },
    });
    return response.data;
  },

  async verifyIntegrationCredentials(platform, integrationKey = 'default') {
    const response = await api.post(`${MGT}/integrations/${platform}/verify`, null, {
      params: { integration_key: integrationKey },
    });
    return response.data;
  },

  /** What Google reports now for this org's Google connections (read-only). */
  async getGoogleLiveStatus() {
    const response = await api.get(`${MGT}/integrations/google/live-status`);
    return response.data;
  },

  async getIntegrationHealth(platform, integrationKey = 'default') {
    const response = await api.get(`${MGT}/integrations/${platform}/health`, {
      params: { integration_key: integrationKey },
    });
    return response.data;
  },

  /** A self-scheduling pull's latest runs, newest first (Netskope). */
  async getIntegrationRuns(platform, integrationKey = 'default', limit = 20) {
    const response = await api.get(`${MGT}/integrations/${platform}/runs`, {
      params: { integration_key: integrationKey, limit },
    });
    return response.data;
  },

  /** How often a self-scheduling pull runs, in seconds (one of the health response's choices). */
  async setIntegrationSchedule(platform, pollIntervalSeconds, integrationKey = 'default') {
    const response = await api.put(`${MGT}/integrations/${platform}/schedule`,
      { poll_interval_seconds: pollIntervalSeconds }, { params: { integration_key: integrationKey } });
    return response.data;
  },

  async getGeminiEnterpriseStatus() {
    const response = await api.get(`${MGT}/integrations/gemini_enterprise/status`);
    return response.data;
  },

  // =========================================================================
  // Integration Destinations (outbound: push enriched events to SIEMs, etc.)
  // =========================================================================

  async getDestinations() {
    const response = await api.get(`${MGT}/integrations/destinations`);
    return response.data;
  },

  async saveDestination(destinationId, config) {
    // Extract top-level fields the backend expects separately from config
    const { data_types, log_filter, delivery_method, ...rest } = config;
    const response = await api.post(`${MGT}/integrations/destinations/${destinationId}`, {
      config: rest,
      data_types: data_types || [],
      log_filter: log_filter || 'threats_only',
      delivery_method: delivery_method || null,
      enabled: true,
    });
    return response.data;
  },

  async toggleDestination(destinationId, enabled) {
    const response = await api.patch(`${MGT}/integrations/destinations/${destinationId}`, {
      enabled,
    });
    return response.data;
  },

  async testDestination(destinationId, testConfig = null) {
    const response = await api.post(`${MGT}/integrations/destinations/${destinationId}/test`, testConfig || {});
    return response.data;
  },

  async removeDestination(destinationId) {
    const response = await api.delete(`${MGT}/integrations/destinations/${destinationId}`);
    return response.data;
  },

  async previewDestinationLog(destinationId, dataTypes) {
    const response = await api.post(`${MGT}/integrations/destinations/${destinationId}/preview`, {
      data_types: dataTypes,
    });
    return response.data;
  },
};
